import { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, onSnapshot, doc, updateDoc, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Bell, X, Check, DollarSign, Package, CreditCard, Users, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

interface AdminNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  userId?: string;
  userName?: string;
  userEmail?: string;
  amount?: number;
  read: boolean;
  createdAt: any;
  paymentId?: string;
  chargeId?: string;
  addonId?: string;
  planId?: string;
}

export default function AdminNotificationCenter() {
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [showPanel, setShowPanel] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('AdminNotificationCenter mounted');
    // Real-time listener for admin notifications
    const q = query(
      collection(db, 'admin_notifications'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const notifs: AdminNotification[] = [];
        snapshot.forEach((doc) => {
          notifs.push({
            id: doc.id,
            ...doc.data(),
          } as AdminNotification);
        });
        
        console.log('Admin notifications loaded:', notifs.length);
        setNotifications(notifs);
        setUnreadCount(notifs.filter(n => !n.read).length);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading admin notifications:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const markAsRead = async (notificationId: string) => {
    try {
      const notifRef = doc(db, 'admin_notifications', notificationId);
      await updateDoc(notifRef, { read: true });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadQuery = query(
        collection(db, 'admin_notifications'),
        where('read', '==', false)
      );
      const snapshot = await getDocs(unreadQuery);
      
      const promises = snapshot.docs.map(doc => 
        updateDoc(doc.ref, { read: true })
      );
      
      await Promise.all(promises);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'addon_purchase':
        return <Package className="w-5 h-5 text-orange-600" />;
      case 'subscription_payment':
        return <CreditCard className="w-5 h-5 text-blue-600" />;
      case 'salepage_payment_approved':
        return <Check className="w-5 h-5 text-green-600" />;
      case 'course_payment':
        return <DollarSign className="w-5 h-5 text-purple-600" />;
      case 'member_approval':
        return <Users className="w-5 h-5 text-teal-600" />;
      default:
        return <Bell className="w-5 h-5 text-gray-600" />;
    }
  };

  const getNotificationLink = (notification: AdminNotification) => {
    // All payment-related notifications go to payment approval page
    if (notification.type === 'addon_purchase' || 
        notification.type === 'subscription_payment' || 
        notification.type === 'subscription_renewal' ||
        notification.type === 'salepage_payment_approved') {
      return `/admin/payment-approval`;
    }
    return '/admin';
  };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'เมื่อสักครู่';
    if (diffMins < 60) return `${diffMins} นาทีที่แล้ว`;
    if (diffHours < 24) return `${diffHours} ชั่วโมงที่แล้ว`;
    if (diffDays < 7) return `${diffDays} วันที่แล้ว`;
    
    return date.toLocaleDateString('th-TH', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="relative">
      {/* Notification Bell Button */}
      <button
        onClick={() => setShowPanel(!showPanel)}
        className="relative p-2 hover:bg-gray-100 rounded-xl transition-colors"
      >
        <Bell className="w-6 h-6 text-gray-700" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {showPanel && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowPanel(false)}
          />

          {/* Panel */}
          <div className="absolute right-0 top-12 w-96 max-h-[600px] bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                <h3 className="font-bold text-lg">การแจ้งเตือน</h3>
                {unreadCount > 0 && (
                  <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs font-semibold">
                    {unreadCount} ใหม่
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowPanel(false)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Mark All as Read */}
            {unreadCount > 0 && (
              <div className="p-3 border-b border-gray-200 bg-gray-50">
                <button
                  onClick={markAllAsRead}
                  className="text-sm text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1"
                >
                  <Check className="w-4 h-4" />
                  ทำเครื่องหมายทั้งหมดว่าอ่านแล้ว
                </button>
              </div>
            )}

            {/* Notifications List */}
            <div className="overflow-y-auto max-h-[500px]">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">กำลังโหลด...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">ไม่มีการแจ้งเตือน</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {notifications.slice(0, 5).map((notification) => (
                    <Link
                      key={notification.id}
                      to={getNotificationLink(notification)}
                      onClick={() => {
                        markAsRead(notification.id);
                        setShowPanel(false);
                      }}
                      className={`block p-4 hover:bg-gray-50 transition-colors ${
                        !notification.read ? 'bg-blue-50/50' : ''
                      }`}
                    >
                      <div className="flex gap-3">
                        <div className="shrink-0 mt-1">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className={`text-sm font-semibold ${
                              !notification.read ? 'text-gray-900' : 'text-gray-700'
                            }`}>
                              {notification.title}
                            </h4>
                            {!notification.read && (
                              <div className="w-2 h-2 bg-blue-600 rounded-full shrink-0 mt-1"></div>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                          {notification.amount && (
                            <p className="text-sm font-semibold text-green-600 mt-1">
                              ฿{notification.amount.toLocaleString()}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <p className="text-xs text-gray-400">
                              {formatTime(notification.createdAt)}
                            </p>
                            <ExternalLink className="w-3 h-3 text-gray-400" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-3">
                <Link
                  to="/admin/unified-payment-approval"
                  onClick={() => setShowPanel(false)}
                  className="block text-center text-sm text-blue-600 hover:text-blue-700 font-semibold"
                >
                  ดูเพิ่มเติม - อนุมัติการชำระเงิน
                </Link>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
